/**
 * URL Utilities for handling image and API URLs across web and Capacitor environments
 *
 * In Capacitor, the app runs on capacitor://localhost, so relative URLs don't work.
 * This utility converts relative URLs to absolute URLs when running in Capacitor.
 */

import { Capacitor } from '@capacitor/core';

/**
 * Production API base URL
 * In production, this should be the actual domain
 * For development, this could be overridden via environment variable
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://yapzap.com';

/**
 * Convert a potentially relative URL to an absolute URL
 *
 * @param url - The URL to convert (can be relative or absolute)
 * @returns Absolute URL string, or null if input was null/undefined
 *
 * @example
 * // On web
 * getAbsoluteUrl('/api/public/images/header.jpg')
 * // Returns: '/api/public/images/header.jpg'
 *
 * // In Capacitor
 * getAbsoluteUrl('/api/public/images/header.jpg')
 * // Returns: 'https://yapzap.com/api/public/images/header.jpg'
 *
 * // Already absolute
 * getAbsoluteUrl('https://example.com/image.jpg')
 * // Returns: 'https://example.com/image.jpg'
 */
export function getAbsoluteUrl(url: string | null | undefined): string | null {
  // Handle null/undefined
  if (!url) return null;

  // If already absolute (starts with http:// or https://), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // If in Capacitor native platform, convert relative URLs to absolute
  if (Capacitor.isNativePlatform()) {
    // Ensure URL starts with /
    const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
    return `${API_BASE_URL}${normalizedUrl}`;
  }

  // On web, relative URLs work fine
  return url;
}

/**
 * Get srcSet attribute value with absolute URLs for responsive images
 * Useful for <img srcSet> attributes in Capacitor
 *
 * @param baseUrl - Base URL for the image
 * @param sizes - Array of sizes (e.g., ['400w', '800w', '1280w'])
 * @returns srcSet string with absolute URLs
 */
export function getAbsoluteSrcSet(
  baseUrl: string | null | undefined,
  sizes: string[]
): string | null {
  if (!baseUrl) return null;

  const absoluteUrl = getAbsoluteUrl(baseUrl);
  if (!absoluteUrl) return null;

  // If we're not in Capacitor or URL is already absolute, return as-is
  if (!Capacitor.isNativePlatform()) {
    return sizes.map(size => `${baseUrl}?w=${size} ${size}`).join(', ');
  }

  // In Capacitor, create absolute URLs for each size
  return sizes.map(size => `${absoluteUrl}?w=${size} ${size}`).join(', ');
}

/**
 * Check if currently running in Capacitor environment
 */
export function isCapacitor(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Get the current platform (web, ios, android)
 */
export function getPlatform(): string {
  return Capacitor.getPlatform();
}

/**
 * Log URL transformation for debugging
 * Only logs in development mode
 */
export function logUrlTransformation(original: string, transformed: string) {
  if (import.meta.env.DEV && Capacitor.isNativePlatform()) {
    console.log('[URL Transform]', { original, transformed });
  }
}

/**
 * Get the public shareable base URL for sharing links
 *
 * This is used when generating URLs to share via messaging/social media.
 * In Capacitor (mobile apps), window.location.origin points to the local
 * Capacitor server, so we need to use the production URL instead.
 *
 * @returns The base URL to use for shareable links (e.g., 'https://yapzap.com')
 *
 * @example
 * const leagueUrl = `${getShareableBaseUrl()}/leagues/${leagueId}`;
 * // In Capacitor: 'https://yapzap.com/leagues/123'
 * // On web prod: 'https://yapzap.com/leagues/123'
 * // On web dev: 'https://127.0.0.1:3000/leagues/123'
 */
export function getShareableBaseUrl(): string {
  // In Capacitor/native apps, always use production URL for sharing
  if (Capacitor.isNativePlatform()) {
    return 'https://yapzap.com';
  }

  // On web, use the current origin (works for both prod and dev)
  return window.location.origin;
}
